'use client'

/**
 * Papr Auth & Onboarding funnel — shared kit (tokens + atoms).
 *
 * Faithful TSX port of the Claude Design handoff
 * (design/handoff-auth-onboarding/onboard/onb-kit.jsx + shared.jsx + payments/pay-data.jsx).
 *
 * Preview-only: this powers /preview-3 as a self-contained, mock-data funnel for
 * design comparison. No real auth/persistence is wired here. Inline styles are used
 * deliberately to match the handoff's exact pixel/weight values.
 */

import React from 'react'

// ---- Tokens ----
export const OB = {
  ink: '#0a0a0b',
  paper: '#fffdf8',
  bg: '#f3f0ea',
  mute: '#7a7468',
  dim: '#a59f93',
  line: 'rgba(0,0,0,0.06)',
  line2: 'rgba(0,0,0,0.10)',
  lime: '#caff33',
  purple: '#7c5cff',
  green: '#10b981',
  greenInk: '#0d8a64',
} as const

export const FONT_SANS = 'var(--font-geist-sans), Geist, system-ui, sans-serif'
export const FONT_MONO = 'var(--font-geist-mono), "Geist Mono", ui-monospace, monospace'

// ---- Shared data types ----
export type InvoiceStatus = 'paid' | 'sent' | 'overdue' | 'draft'

export type LineItem = { desc: string; qty: number | string; rate: number | string }

export type OnbEntity = {
  name: string
  country: string
  currency: string
  address: string
  logo: string | null
  taxId?: string
  displayName?: string
  vatValidation?: import('@/lib/validation/vat.schemas').VatValidation
}

export type OnbInvoice = {
  client: string
  email: string
  items: LineItem[]
  due: string
  tax: boolean
}

export type TeamMember = { email: string; role: string }

export type OnbData = {
  role: string | null
  uses: string[]
  entity: OnbEntity
  team: TeamMember[]
  invoice: OnbInvoice | null
  invoiceSent: boolean
}

// ---- Icon (Material-style single-path SVG) ----
export function Icon({ d, size = 16, color = 'currentColor' }: { d: string; size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0 }}>
      <path d={d} />
    </svg>
  )
}

// ---- Sparkle (4-point AI star) ----
export function Sparkle({ size = 12, color = OB.purple }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z" fill={color} />
    </svg>
  )
}

// ---- Brand wordmark ----
export function PaprMark({ accent = '#0b0b0c', dot = OB.purple, size = 22 }: { accent?: string; dot?: string; size?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 1, fontFamily: FONT_SANS, fontWeight: 700, fontSize: size, letterSpacing: '-0.03em', color: accent, lineHeight: 1 }}>
      <span>papr</span>
      <span style={{ width: size * 0.22, height: size * 0.22, borderRadius: '50%', background: dot, display: 'inline-block', marginLeft: 1 }}></span>
    </div>
  )
}

// ---- Status pill ----
export function StatusPill({ status }: { status: InvoiceStatus }) {
  const labels: Record<InvoiceStatus, string> = { paid: 'Paid', sent: 'Sent', overdue: 'Overdue', draft: 'Draft' }
  const colors: Record<InvoiceStatus, { bg: string; fg: string; dot: string }> = {
    paid: { bg: 'rgba(16,185,129,0.12)', fg: '#047857', dot: '#10b981' },
    sent: { bg: 'rgba(14,165,233,0.12)', fg: '#0369a1', dot: '#0ea5e9' },
    overdue: { bg: 'rgba(239,68,68,0.12)', fg: '#b91c1c', dot: '#ef4444' },
    draft: { bg: 'rgba(156,163,175,0.18)', fg: '#4b5563', dot: '#9ca3af' },
  }
  const c = colors[status]
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px 3px 7px', borderRadius: 999, background: c.bg, color: c.fg, fontSize: 11, fontWeight: 600, letterSpacing: '0.01em' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot }}></span>
      {labels[status]}
    </span>
  )
}

// ---- Spinner ----
export function Spinner({ color = OB.ink, size = 18 }: { color?: string; size?: number }) {
  return <span style={{ width: size, height: size, borderRadius: '50%', border: '2.5px solid rgba(0,0,0,0.2)', borderTopColor: color, display: 'inline-block', animation: 'paySpin .8s linear infinite' }}></span>
}

// ---- Entity mark ----
export function EntityMark({ color, dot, size = 32, radius = 9 }: { color: string; dot: string; size?: number; radius?: number }) {
  return (
    <div style={{ width: size, height: size, borderRadius: radius, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ width: size * 0.26, height: size * 0.26, borderRadius: '50%', background: dot }}></span>
    </div>
  )
}

// ---- "Secured by Stripe" lockup (text mark only) ----
export function SecuredByStripe({ color = OB.dim, size = 11 }: { color?: string; size?: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: size, color, fontFamily: FONT_MONO, letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>
      <svg width={size + 1} height={size + 1} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <path d="M12 2l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V5l7-3z" fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Secured by Stripe
    </span>
  )
}

// ---- Money formatting ----
export function payMoney(n: number, sym = '€') {
  const neg = n < 0
  const v = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return (neg ? '−' : '') + sym + v
}

// ---- Form field wrapper ----
export function OnbField({ label, hint, optional, children }: { label: React.ReactNode; hint?: React.ReactNode; optional?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: OB.ink, letterSpacing: '-0.01em' }}>{label}</span>
        {optional && <span style={{ fontSize: 11, color: OB.dim, fontFamily: FONT_MONO }}>optional</span>}
        {hint && <span style={{ marginLeft: 'auto', fontSize: 11, color: OB.dim }}>{hint}</span>}
      </div>
      {children}
    </label>
  )
}

// ---- Text input ----
export function OnbInput({
  value, onChange, placeholder, type = 'text', autoFocus, onKeyDown, prefix, size = 'md', name, readOnly,
}: {
  value: string
  onChange?: (v: string) => void
  placeholder?: string
  type?: string
  autoFocus?: boolean
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  prefix?: React.ReactNode
  size?: 'md' | 'lg'
  name?: string
  readOnly?: boolean
}) {
  const h = size === 'lg' ? 50 : 44
  return (
    <div className="onb-inwrap" style={{ display: 'flex', alignItems: 'center', height: h, borderRadius: 11, border: `1px solid ${OB.line2}`, background: readOnly ? OB.bg : '#fff', overflow: 'hidden' }}>
      {prefix && <span style={{ paddingLeft: 14, color: OB.dim, display: 'flex', alignItems: 'center' }}>{prefix}</span>}
      <input
        className="onb-in" name={name} type={type} value={value} placeholder={placeholder}
        autoFocus={autoFocus} onKeyDown={onKeyDown} readOnly={readOnly}
        onChange={(e) => onChange && onChange(e.target.value)}
        style={{ flex: 1, height: '100%', border: 'none', outline: 'none', background: 'transparent', padding: prefix ? '0 14px 0 10px' : '0 14px', fontSize: 14.5, fontFamily: 'inherit', color: readOnly ? OB.mute : OB.ink, minWidth: 0, cursor: readOnly ? 'default' : undefined }}
      />
    </div>
  )
}

// ---- Select ----
export function OnbSelect({ value, onChange, children, size = 'md' }: { value: string; onChange?: (v: string) => void; children: React.ReactNode; size?: 'md' | 'lg' }) {
  const h = size === 'lg' ? 50 : 44
  return (
    <div className="onb-inwrap" style={{ position: 'relative', height: h, borderRadius: 11, border: `1px solid ${OB.line2}`, background: '#fff' }}>
      <select
        value={value} onChange={(e) => onChange && onChange(e.target.value)}
        style={{ width: '100%', height: '100%', border: 'none', outline: 'none', background: 'transparent', padding: '0 38px 0 14px', fontSize: 14.5, fontFamily: 'inherit', color: OB.ink, appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }}
      >
        {children}
      </select>
      <span style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
        <Icon d="M7 10l5 5 5-5z" size={14} color={OB.dim} />
      </span>
    </div>
  )
}

// ---- Primary (lime) button ----
export function OnbPrimary({
  children, onClick, disabled, full, arrow, type = 'button', size = 'md',
}: {
  children: React.ReactNode
  onClick?: () => void
  disabled?: boolean
  full?: boolean
  arrow?: boolean
  type?: 'button' | 'submit'
  size?: 'md' | 'lg'
}) {
  const h = size === 'lg' ? 50 : 44
  return (
    <button
      type={type} onClick={onClick} disabled={disabled} className="onb-primary"
      style={{ height: h, padding: '0 22px', borderRadius: 11, border: 'none', background: disabled ? 'rgba(0,0,0,0.08)' : OB.lime, color: disabled ? OB.dim : OB.ink, fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.01em', cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', width: full ? '100%' : 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}
    >
      {children}
      {arrow && <Icon d="M5 12h14M13 6l6 6-6 6" size={16} color={disabled ? OB.dim : OB.ink} />}
    </button>
  )
}

// ---- Secondary / ghost ----
export function OnbGhost({ children, onClick, kind = 'outline', size = 'md', full }: { children: React.ReactNode; onClick?: () => void; kind?: 'outline' | 'plain'; size?: 'md' | 'lg'; full?: boolean }) {
  const h = size === 'lg' ? 50 : 44
  const sty: React.CSSProperties = kind === 'plain'
    ? { background: 'transparent', border: '1px solid transparent', color: OB.mute }
    : { background: '#fff', border: `1px solid ${OB.line2}`, color: OB.ink }
  return (
    <button type="button" onClick={onClick} className="onb-ghost" style={{ height: h, padding: '0 18px', borderRadius: 11, ...sty, width: full ? '100%' : 'auto', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      {children}
    </button>
  )
}

// ---- Selectable card (role / use-case) ----
export function OnbSelectCard({
  selected, onClick, glyph, glyphBg, title, sub, multi,
}: {
  selected?: boolean
  onClick?: () => void
  glyph: React.ReactNode
  glyphBg?: string
  title: string
  sub?: string
  multi?: boolean
}) {
  return (
    <button
      type="button" onClick={onClick} className="onb-card"
      style={{ textAlign: 'left', display: 'flex', alignItems: 'flex-start', gap: 13, padding: '15px 16px', borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit', width: '100%', background: selected ? '#fff' : OB.paper, border: `1.5px solid ${selected ? OB.ink : OB.line2}`, boxShadow: selected ? '0 4px 16px rgba(0,0,0,0.06)' : 'none' }}
    >
      <div style={{ width: 40, height: 40, borderRadius: 11, background: glyphBg || 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {glyph}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: OB.ink, letterSpacing: '-0.01em' }}>{title}</div>
        {sub && <div style={{ fontSize: 12.5, color: OB.mute, marginTop: 3, lineHeight: 1.45 }}>{sub}</div>}
      </div>
      <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: multi ? 6 : '50%', border: `1.5px solid ${selected ? OB.ink : OB.line2}`, background: selected ? OB.ink : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1, transition: 'all .12s' }}>
        {selected && <Icon d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" size={12} color={OB.lime} />}
      </span>
    </button>
  )
}

// ---- Multi-select chip ----
export function OnbChip({ selected, onClick, label, glyph }: { selected?: boolean; onClick?: () => void; label: string; glyph: React.ReactNode }) {
  return (
    <button
      type="button" onClick={onClick} className="onb-chip"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 14px 9px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 500, background: selected ? OB.ink : '#fff', color: selected ? '#fff' : OB.ink, border: `1px solid ${selected ? OB.ink : OB.line2}` }}
    >
      <span style={{ display: 'flex', opacity: selected ? 1 : 0.7 }}>{glyph}</span>
      {label}
      {selected && <Icon d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" size={12} color={OB.lime} />}
    </button>
  )
}

// ---- Tiny pill / tag ----
export function OnbTag({ children, color = OB.greenInk, bg = 'rgba(16,185,129,0.12)' }: { children: React.ReactNode; color?: string; bg?: string }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, background: bg, color, fontSize: 11, fontWeight: 600, fontFamily: FONT_MONO }}>{children}</span>
}
